import { useInterval } from 'ahooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Row from 'react-bootstrap/Row';
import Tooltip from 'react-bootstrap/Tooltip';
import { useTranslation } from 'react-i18next';
import { FaClock } from 'react-icons/fa';

import { dailypriceActions, preferenceActions } from '../../../actions';
import { uiActions } from '../../../actions';
import HKEXNews from '../../../assets/hkex-news.svg';
import HeadOverride from '../../../components/general/HeadOverride';
import { regionToCategory } from '../../../components/stock/stockCategory';
import {
  ccassShortcutColumn,
  changeColumnV2,
  changePercentageColumnID,
  changePercentageColumnV2,
  concentrationColumn,
  indexColumn,
  relPriceColumnV2,
  relVolumnColumnV2,
  totalNoColumnV2,
} from '../../../components/table/columns';
import DLStockPeek from '../../../components/table/DLStockPeek';
import DLTable from '../../../components/table/DLTable/DLTable';
import {
  ConcentrationFilter,
  NumberFilter,
  ParticipantNumChangeFilter,
  PriceChangeFilter,
  PriceFilter,
  PriceRangeFilter,
} from '../../../components/table/filters';
import HKNewsCell from '../../../components/table/HKNewsCell';
import TagSelector from '../../../components/table/TagSelector';
import { Breakpoints, FreeUserAlertEnum } from '../../../enums';
import { uiHelpers } from '../../../helpers';
import { usePageVisitAnalytics } from '../../../helpers/analytics.helper';
import { useStockColumnProps } from '../../../helpers/table.helper';
import { useTagEvaluation } from '../../../helpers/tag.helpper';
import { dateToDateStr } from '../../../helpers/ui.helper';
import useDispatch from '../../../hooks/useDispatch';
import { usePreferences } from '../../../hooks/usePreferences';
import useRegion from '../../../hooks/useRegion';
import { useUser } from '../../../hooks/users';
import useSelector from '../../../hooks/useSelector';
import { useUserStockList } from '../../../hooks/useUserStockList';
import FeatureRequiresVIPAlert from '../../../modules/FreeUser/FeatureRequiresVIPAlert';
import { Category } from '../../../py-generated/datalouder/stock/types';
import { Region } from '../../../shared/types';
import { ReactTable } from '../../../types';
import BlurPlaceholder from '../../FreeUser/BlurPlaceholder';
import PurchaseVIPFloatButton from '../../FreeUser/PurchaseVIPFloatButton';
import UpdatedAtLabel from '../../General/UpdatedAtLabel';
import TutorialQuestionMark from '../../Help/TutorialQuestionMark';
import IndexBar from '../../Ranking/IndexBar';
import GainLostChart from './GainLostChart';
import UserListBar from './UserListBar';

export const useStatisticsColumns = (
  realtime: boolean,
  region: Region,
  includeConcentration?: boolean
) => {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= Breakpoints.small
  );

  const handleResize = useCallback(() => {
    const windowSize = window.innerWidth;
    setIsMobile(windowSize <= Breakpoints.small);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const stockCodeColumn = useStockColumnProps(region);

  const {
    priceChangeInterval,
    priceRangeInterval,
    avgVolInterval,
    participantNumChangeInterval,
    topN,
  } = usePreferences();

  const { t, i18n } = useTranslation();

  return useMemo(() => {
    let _columns: ReactTable.Column[] = [
      indexColumn(),
      {
        id: 'clock',
        desktopOnly: true,
        realtimeOnly: true,
        Header: () => <FaClock />,
        width: 75,
        disableFilters: true,
        accessor: item => item['ref']['price_time'],
        Cell: ({ cell: { value } }) => (
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="last-transaction-time-tooltip">
                {t('last-transaction-time')}
              </Tooltip>
            }
          >
            <div>{uiHelpers.formatTimeLocalFromTimestamp(value)}</div>
          </OverlayTrigger>
        ),
      },
      {
        ...stockCodeColumn,
        id: `${region}-ranking-stockCode`,
        sticky: isMobile ? 'left' : undefined,
      },
      {
        id: `${region}-ranking-price`,
        Header: t('Price'),
        accessor: item => item['ref']['price'],
        width: 90,
        Cell: ({ cell: { value } }) => (
          <div>{uiHelpers.formatMoney(value)}</div>
        ),
        Filter: PriceFilter,
        sortDescFirst: true,
      },
      changeColumnV2(t, priceChangeInterval, PriceChangeFilter),
      changePercentageColumnV2(t, priceChangeInterval),
      relVolumnColumnV2(t, avgVolInterval),
      {
        id: `${region}-ranking-turnover`,
        hkOnly: true,
        Header: (
          <span>
            {t('Turnover')} <TutorialQuestionMark tutorialId={21} />
          </span>
        ),
        accessor: item => item.ref.turnover,
        width: 100,
        Cell: ({ cell: { value } }) => (
          <div>{uiHelpers.formatLargeMoney(i18n, value)}</div>
        ),
        Filter: NumberFilter,
        sortDescFirst: true,
      },
      {
        id: `${region}-ranking-turnover`,
        usOnly: true,
        Header: (
          <span>
            {t('Turnover')} <TutorialQuestionMark tutorialId={21} />
          </span>
        ),
        accessor: item => item.ref.turnover,
        width: 100,
        Cell: ({ cell: { value } }) => (
          <div>{uiHelpers.formatLargeMoney(i18n, value)}</div>
        ),
        sortDescFirst: true,
      },
      {
        id: `${region}-ranking-mktcap`,
        Header: (
          <span>
            {t('Mktcap')} <TutorialQuestionMark tutorialId={21} />
          </span>
        ),
        // React table doesn't work properly if some values are undefined
        // don't know why.
        accessor: item => item.ref.mktcap || null,
        width: 100,
        Cell: ({ cell: { value } }) => (
          <div>{uiHelpers.formatLargeMoney(i18n, value)}</div>
        ),
        sortDescFirst: true,
      },
      totalNoColumnV2(
        t,
        participantNumChangeInterval,
        ParticipantNumChangeFilter
      ),
      ...(includeConcentration
        ? [concentrationColumn(t, topN, ConcentrationFilter)]
        : []),
      relPriceColumnV2(t, priceRangeInterval, PriceRangeFilter),
      {
        id: `${region}-ranking-hkexnews`,
        Header: () => <img src={HKEXNews} style={{ height: '1rem' }} />,
        hkOnly: true,
        width: 130,
        sortDescFirst: true,
        // React table doesn't work properly if some values are null
        // don't know why.
        accessor: item => item?.newsTime || 0,
        Cell: HKNewsCell,
        contentAlign: 'left',
        disableFilters: true,
        disableExport: true,
      },
      ccassShortcutColumn(),
    ];

    if (region !== 'hk') _columns = _columns.filter(({ hkOnly }) => !hkOnly);
    if (region !== 'us') _columns = _columns.filter(({ usOnly }) => !usOnly);
    if (region === 'hk') _columns = _columns.filter(({ notHK }) => !notHK);
    if (!realtime)
      _columns = _columns.filter(({ realtimeOnly }) => !realtimeOnly);

    // filter out desktop only columns for mobile screen size
    if (isMobile) {
      _columns = _columns.filter(props => !props.desktopOnly);
    }
    return _columns;
  }, [
    avgVolInterval,
    i18n,
    includeConcentration,
    isMobile,
    participantNumChangeInterval,
    priceChangeInterval,
    priceRangeInterval,
    realtime,
    region,
    stockCodeColumn,
    t,
    topN,
  ]);
};

const useGainLostRatio = () => {
  const { statistics } = useSelector(state => state.dailyprice.statistics);

  let nGain = 0,
    nLoss = 0,
    nEqual = 0;
  if (statistics) {
    const diff = statistics.map(
      item => item.ref.price - item.price_changes['1d'][0]
    );
    nGain = diff.filter(n => n > 0).length;
    nLoss = diff.filter(n => n < 0).length;
    nEqual = diff.filter(n => n === 0).length;
  }

  return {
    nGain,
    nLoss,
    nEqual,
  };
};

const useFilteredStatistics = (category: Category) => {
  const {
    view: { userId: viewingUserId },
  } = useSelector(state => state.stocklist);

  const { users } = useUserStockList();

  const tagEvalFn = useTagEvaluation();

  const { statistics } = useSelector(state => state.dailyprice.statistics);

  if (!statistics) {
    return statistics;
  }

  let filtered = statistics;
  if (users && viewingUserId) {
    const viewingUser = users.find(user => user.userId === viewingUserId);

    // checking viewingUser to make sure viewingUser did not
    // set the profile private after being viewed.
    if (viewingUser) {
      filtered = filtered.filter(({ stockCode }) =>
        viewingUser.stocks.includes(stockCode)
      );
      filtered = filtered.map(v => {
        const arrayIndex = viewingUser.stocks.indexOf(v.stockCode);
        return { ...v, unlike: viewingUser.unlikes[arrayIndex] };
      });
    }
  }

  filtered = filtered.filter(({ stockCode }) =>
    tagEvalFn(category + '#' + stockCode)
  );

  // if viewing user or viewing tags, skip category selection
  if (filtered !== statistics) {
    return filtered;
  }

  return statistics;
};

const RankingPage: React.FC = () => {
  usePageVisitAnalytics();

  const [date, setDate] = useState<Date>(new Date());

  const dispatch = useDispatch();
  const region = useRegion();
  const category = regionToCategory(region);
  const { i18n, t } = useTranslation();

  const user = useUser();

  const fetch = useCallback(() => {
    if (user) {
      dispatch(
        dailypriceActions.fetchStatistics(
          category,
          i18n.language,
          dateToDateStr(uiHelpers.swapLocalToUTCDate(date, region))
        )
      );
      dispatch(dailypriceActions.fetchHistograms(region));
    }
  }, [category, date, dispatch, i18n.language, region, user]);

  useInterval(() => {
    if (dateToDateStr(date) === dateToDateStr(new Date())) {
      // stop auto refresh when date is not today
      fetch();
    }
  }, 120_000);

  useEffect(fetch, [fetch]);

  const uploadFilters = useCallback(
    (filters: any[]) => {
      dispatch(preferenceActions.setStatisticsPageFilters(region, filters));
    },
    [dispatch, region]
  );

  const { statistics, loading, when, updatedAt, realtime } = useSelector(
    state => state.dailyprice.statistics
  );

  const { usStatisticsPageFilters, hkStatisticsPageFilters } = usePreferences();
  const { showParticipantNumberChangeFilterAlert, showPriceRangeFilterAlert } =
    useSelector(state => state.ui.freeUserAlert);

  const columns: ReactTable.Column[] = useStatisticsColumns(realtime, region);

  const filteredStatistics = useFilteredStatistics(category);

  const { nGain, nLoss, nEqual } = useGainLostRatio();

  const tickersWithCategory: Set<string> = useMemo(
    () =>
      statistics
        ? statistics.map(item => region.toUpperCase() + '#' + item['stockCode'])
        : new Set(),
    [region, statistics]
  );

  return (
    <Container fluid={!!user}>
      <HeadOverride>Ranking</HeadOverride>
      {!user && <PurchaseVIPFloatButton />}
      <BlurPlaceholder featureKey={'statistic'} showContent={!!user}>
        <IndexBar />
        <Row>
          <Col md={6}>
            <UpdatedAtLabel
              updatedAt={updatedAt}
              when={when}
              hasDatePicker
              datePickerHoverText={t('one-month-data-available')}
              dateChanged={setDate}
              hasRefresh={realtime}
              loading={loading}
            />
          </Col>
          <Col md={6}>
            <UserListBar />
          </Col>
        </Row>
        <Row>
          <Col className="ranking-progress-bar">
            {statistics && (
              <GainLostChart
                n={statistics.length}
                nGain={nGain}
                nLoss={nLoss}
                nEqual={nEqual}
              />
            )}
          </Col>
        </Row>
        <Row>
          <Col>
            <TagSelector tickersWithCategory={tickersWithCategory} />
          </Col>
        </Row>
        <DLTable
          tableId="daily-ranking"
          columns={columns}
          data={filteredStatistics || []}
          loading={loading}
          renderRowSubComponent={({
            mode,
            row: {
              original: { stockCode },
            },
          }) => {
            return (
              <DLStockPeek stockCode={stockCode} mode={mode} region={region} />
            );
          }}
          defaultSortBy={[
            {
              id: changePercentageColumnID,
              desc: true,
            },
          ]}
          defaultFiltered={
            region === 'us' ? usStatisticsPageFilters : hkStatisticsPageFilters
          }
          allowExport
          exportFileName={`stock-ranking-${region}`}
          onFiltersChanged={uploadFilters}
        />
        {region === 'hk' && (
          <p>
            <small>{t('* All CCASS data is 3 trading days old.')}</small>
          </p>
        )}
        <PurchaseVIPFloatButton appendText={t('and-enable-realtime-view')} />
        {showParticipantNumberChangeFilterAlert && (
          <FeatureRequiresVIPAlert
            featureTitle={t('N Level Filter')}
            onHide={() =>
              dispatch(
                uiActions.freeUserAlert.setFreeUserAlert(
                  FreeUserAlertEnum.showParticipantNumberChangeFilterAlert,
                  false
                )
              )
            }
          ></FeatureRequiresVIPAlert>
        )}
        {showPriceRangeFilterAlert && (
          <FeatureRequiresVIPAlert
            featureTitle={t('Price Range Filter')}
            onHide={() =>
              dispatch(
                uiActions.freeUserAlert.setFreeUserAlert(
                  FreeUserAlertEnum.showPriceRangeFilterAlert,
                  false
                )
              )
            }
          />
        )}
      </BlurPlaceholder>
    </Container>
  );
};

export default RankingPage;
